import { useQuery } from "@tanstack/react-query";
import { useCostDashboard, type CostCodeData } from "./useCostDashboard";
import { useCommitments } from "./useCommitments";
import { useDailyLogs } from "./useDailyLogs";
import { useFinancialSummary } from "./useCommitments";

export type AlertType = "BUDGET_OVER" | "COMMITMENT_GAP" | "PENDING_LOGS" | "EXPIRING_COMMITMENT";

export type AlertSeverity = "critical" | "warning" | "info";

export interface ProjectAlert {
  id: string;
  type: AlertType;
  severity: AlertSeverity;
  title: string;
  message: string;
  actionUrl?: string;
  metadata?: Record<string, unknown>;
}

export interface ProjectAlertsResponse {
  alerts: ProjectAlert[];
  totalCount: number;
  criticalCount: number;
  warningCount: number;
  infoCount: number;
}

/**
 * Hook to fetch and compute project alerts
 *
 * Alert types:
 * - BUDGET_OVER: When actual > budget for a cost code
 * - COMMITMENT_GAP: When committed < 90% of budget and project is >30% complete
 * - PENDING_LOGS: Daily logs from field needing PM review
 * - EXPIRING_COMMITMENT: Commitments expiring within 7 days
 */
export function useProjectAlerts(projectId: string) {
  const costDashboard = useCostDashboard(projectId);
  const commitments = useCommitments(projectId);
  const dailyLogs = useDailyLogs(projectId);
  const financialSummary = useFinancialSummary(projectId);

  return useQuery({
    queryKey: ["projects", projectId, "alerts"],
    queryFn: async (): Promise<ProjectAlertsResponse> => {
      const alerts: ProjectAlert[] = [];

      // 1. BUDGET_OVER alerts
      if (costDashboard.data?.byCostCode) {
        const overBudgetCodes = costDashboard.data.byCostCode.filter(
          (cc: CostCodeData) => cc.status === "over"
        );

        for (const costCode of overBudgetCodes) {
          const overAmount = Math.abs(costCode.variance);
          alerts.push({
            id: `budget-over-${costCode.costCodeId}`,
            type: "BUDGET_OVER",
            severity: overAmount > costCode.budget * 0.1 ? "critical" : "warning",
            title: `${costCode.name}: $${overAmount.toLocaleString()} over budget`,
            message: `Cost code ${costCode.code} has exceeded budget by $${overAmount.toLocaleString()}`,
            actionUrl: `/projects/${projectId}/cost-dashboard`,
            metadata: {
              costCodeId: costCode.costCodeId,
              costCode: costCode.code,
              costCodeName: costCode.name,
              budget: costCode.budget,
              actuals: costCode.actuals,
              variance: costCode.variance,
            },
          });
        }
      }

      // 2. COMMITMENT_GAP alerts
      if (financialSummary.data?.summary && costDashboard.data?.summary) {
        const { percentCommitted, percentSpent } = financialSummary.data.summary;

        // Alert if project is >30% complete but commitments are <90% of budget
        if (percentSpent > 0.30 && percentCommitted < 0.90) {
          const gapPercent = ((0.90 - percentCommitted) * 100).toFixed(0);
          alerts.push({
            id: "commitment-gap",
            type: "COMMITMENT_GAP",
            severity: percentCommitted < 0.70 ? "warning" : "info",
            title: `Commitment gap: ${gapPercent}% of budget not committed`,
            message: `Project is ${(percentSpent * 100).toFixed(0)}% spent but only ${(percentCommitted * 100).toFixed(0)}% committed`,
            actionUrl: `/projects/${projectId}/commitments`,
            metadata: {
              percentCommitted,
              percentSpent,
              gapPercent: parseFloat(gapPercent),
            },
          });
        }
      }

      // 3. PENDING_LOGS alerts
      if (dailyLogs.data?.dailyLogs) {
        const pendingLogs = dailyLogs.data.dailyLogs.filter(
          (log) => log.isPending || log.syncStatus === "pending"
        );

        if (pendingLogs.length > 0) {
          alerts.push({
            id: "pending-logs",
            type: "PENDING_LOGS",
            severity: pendingLogs.length > 5 ? "warning" : "info",
            title: `${pendingLogs.length} pending daily log${pendingLogs.length === 1 ? "" : "s"} to review`,
            message: `${pendingLogs.length} daily log${pendingLogs.length === 1 ? "" : "s"} from the field need${pendingLogs.length === 1 ? "s" : ""} review`,
            actionUrl: `/projects/${projectId}/daily-logs`,
            metadata: {
              count: pendingLogs.length,
              logIds: pendingLogs.map((log) => log.id),
            },
          });
        }
      }

      // 4. EXPIRING_COMMITMENT alerts (commitments expiring within 7 days)
      if (commitments.data?.commitments) {
        // Note: Current schema doesn't have expiresAt field for commitments
        // This is a placeholder for future enhancement
        // For now, we'll skip this alert type
      }

      // Sort alerts by severity
      const severityOrder: Record<AlertSeverity, number> = {
        critical: 0,
        warning: 1,
        info: 2,
      };
      alerts.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

      // Compute counts
      const criticalCount = alerts.filter((a) => a.severity === "critical").length;
      const warningCount = alerts.filter((a) => a.severity === "warning").length;
      const infoCount = alerts.filter((a) => a.severity === "info").length;

      return {
        alerts,
        totalCount: alerts.length,
        criticalCount,
        warningCount,
        infoCount,
      };
    },
    enabled: !!projectId && !!(
      costDashboard.data ||
      commitments.data ||
      dailyLogs.data ||
      financialSummary.data
    ),
    staleTime: 1000 * 60 * 2, // 2 minutes - more frequent than other data
    // Depend on underlying data queries
    select: (data) => data,
  });
}
